import WithLoadingIndicator from 'components/page-loader/page-loader';
import { useState } from 'react';

import '../../pages/editor/gridStyles.less';
import './legacy/css/froala_editor.pkgd.min.css';
import './legacy/css/froala_style.css';

import { Global } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';

import type {
  AllTextDefaultStylesType,
  TableTextDefaultTagSelectors,
  TextDefaultTagSelectors,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import {
  sharedTableStyles,
  TableDefaultClassNamesEnum,
  tableTextDefaultStyles as tableTextDefaultStylesDefaultValues,
  TextDefaultSelectorsEnum,
  textDefaultStyles as textDefaultStylesDefaultValues,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { tiptapDefaultStyle } from 'components/editor/GridDndEditor/TiptapEditor/tiptapDefaultStyle.ts';
import { NavigateToCinder } from 'components/navigate-to-cinder';
import { PreviewGrid } from 'components/preview';
import { getCanvasMinHeight, getCanvasWidth } from 'components/editor/shared/gridConfig';
import type { PageOrientation } from 'components/editor/shared/gridConfig';

import { DOWNLOADED_FONTS } from 'components/editor/grid/reduxStore/defaultTextDefaultValue.ts';
import { tableRowDefaultStyle as tableRowDefaultStyleDefaultValues } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice.ts';
import { mapTableStylesToAllBlocks } from 'components/editor/TextDefaultStylesLoader/mapTableStylesToAllBlocks';
import { CINDER_URLS } from 'utils/cinder-urls';
import { pricingTableTotalStyles } from '../../components/editor/TextDefaultStylesLoader/TextDefaultStylesLoader.tsx';
import DefaultFontLoader from '../../components/font/DefaultFontLoader.tsx';
import FontLoader from '../../components/font/index.tsx';
import ProposifyTheme from '../../muiTheme/PyTheme.ts';
import type { SectionByDocumentIdApiResponse } from '../../services/documents/DocumentRepository.ts';
import { useGetContentStyleByContentId } from '../editor/useGetContentStyleByContentId.tsx';
import PreviewContentSections from './PreviewContentSections.tsx';
import { useContentSectionsForPreview } from './useContentSectionsForPreview.tsx';

type AllStyles = Record<string, AllTextDefaultStylesType>;

export function groupTextAndTableTextStyles(styles: AllStyles) {
  const textKeys = Object.values(TextDefaultSelectorsEnum) as string[];
  const tableTextKeys = Object.values(TableDefaultClassNamesEnum) as string[];

  const textStyles: Partial<Record<TextDefaultTagSelectors, AllTextDefaultStylesType>> = {};
  const tableTextStyles: Partial<Record<TableDefaultClassNamesEnum, AllTextDefaultStylesType>> = {};

  Object.entries(styles).forEach(([key, value]) => {
    if (textKeys.includes(key)) {
      textStyles[key as TextDefaultTagSelectors] = value;
    } else if (tableTextKeys.includes(key)) {
      tableTextStyles[key as TableTextDefaultTagSelectors] = value;
    }
  });

  return { textStyles, tableTextStyles };
}

export const PreviewGridContentPage = ({ contentId }: { contentId: string }) => {
  const [, setPreviewConfig] = useState<any>(undefined);

  const { sections: sectionData, sectionsQueryStatus: gridQueryStatus } = useContentSectionsForPreview();
  const sections: SectionByDocumentIdApiResponse[] = sectionData ? sectionData.sections : [];
  const { data: documentStyleApiResponse, status: documentStyleQueryStatus } = useGetContentStyleByContentId(contentId);
  const [isFontLoaded, setIsFontLoaded] = useState<boolean>(false);

  const defaultTextStylingFonts = DOWNLOADED_FONTS.map((font) => ({
    path: font.path,
  }));

  let documentStyle: Record<string, any> = {};
  if (documentStyleQueryStatus === 'success') {
    const { textDefaultStyles, tableDefaultStyles, ...documentSettingStyle } = documentStyleApiResponse;
    const { textStyles, tableTextStyles } = groupTextAndTableTextStyles(
      (textDefaultStyles || { ...tableTextDefaultStylesDefaultValues, ...textDefaultStylesDefaultValues }) as AllStyles
    );

    const { textBlockTableStyles, numberBlockStyles } = mapTableStylesToAllBlocks({
      ...tableTextDefaultStylesDefaultValues,
      ...tableTextStyles,
    });

    documentStyle = {
      canvas: documentSettingStyle,
      textStyle: {
        '.editor__page__draggable .tiptap': {
          ...textStyles,
          ...textBlockTableStyles,
        },
        '.editor__page__draggable': {
          '.py-darkGrey-row .tiptap': { ...sharedTableStyles, fontWeight: 'bold' },
          '.py-total-row .tiptap p:not([data-table-style])': pricingTableTotalStyles,
          '.py-subtotal-row .tiptap p:not([data-table-style])': pricingTableTotalStyles,
          ...numberBlockStyles,
        },
        ...tiptapDefaultStyle,
      },
      tableRowStyles: {
        ':root {': {
          ...tableRowDefaultStyleDefaultValues,
          ...tableDefaultStyles,
        },
      },
    };
  }
  const editorConfigHandler = (newConfig) => {
    setPreviewConfig(newConfig);
  };

  switch (documentStyleQueryStatus) {
    case 'error':
      return <NavigateToCinder path={CINDER_URLS.error} />;
    case 'success': {
      if (gridQueryStatus === 'error') return <NavigateToCinder path={CINDER_URLS.error} />;

      if (!isFontLoaded) {
        return (
          <>
            <WithLoadingIndicator isLoading={true} isLoaded={false} />
            <FontLoader contentId={contentId} editorConfigHandler={editorConfigHandler} fontsFetchedCb={() => setIsFontLoaded(true)} />
          </>
        );
      }

      if (gridQueryStatus === 'pending' || gridQueryStatus === 'loading') {
        return <WithLoadingIndicator isLoading={true} isLoaded={false} />;
      }

      const canvasOrientation = (documentStyle.canvas?.pageOrientation as PageOrientation) ?? 'portrait';
      const canvasWidth = getCanvasWidth(canvasOrientation);
      const canvasMinHeight = getCanvasMinHeight(canvasOrientation);
      return (
        <ThemeProvider theme={ProposifyTheme}>
          <DefaultFontLoader defaultFonts={defaultTextStylingFonts} />
          <FontLoader contentId={contentId} editorConfigHandler={editorConfigHandler} />
          <PreviewGrid
            canvasWidth={canvasWidth}
            canvasMinHeight={canvasMinHeight}
            orientation={canvasOrientation}
            contentId={contentId}
            main={
              <>
                <Global styles={documentStyle.textStyle} />
                <Global styles={documentStyle.tableRowStyles} />
                <PreviewContentSections documentStyle={documentStyle.canvas} sections={sections} isDocumentLocked={true} isTemplate />
              </>
            }
          />
        </ThemeProvider>
      );
    }
    case 'pending':
    default:
      return <WithLoadingIndicator isLoading={true} isLoaded={false}></WithLoadingIndicator>;
  }
};
